/**
 * ao Wishlist (自前実装 / Swym Wishlist Plus 代替)
 * - localStorage ベース（ログイン不要・アプリ不要）
 * - 保存形式: ao:wishlist:v1 = [{ h: handle, id: productId }]
 * - カスタム要素:
 *   <wishlist-toggle data-product-handle data-product-id> … ハートボタン
 *   <wishlist-header-icon> … ヘッダーアイコン（件数バッジ＋塗りつぶし）
 *   <wishlist-grid data-money-format> … お気に入り一覧ページ
 * - タブ間同期: storage イベント / 同一ページ内同期: ao:wishlist:change イベント
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'ao:wishlist:v1';
  const CHANGE_EVENT = 'ao:wishlist:change';

  /* ---------------- Core store ---------------- */
  const Wishlist = {
    read() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.filter((x) => x && x.h) : [];
      } catch {
        return [];
      }
    },
    write(list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch {
        /* private mode 等では保存不可。UI は動作継続 */
      }
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
    },
    has(handle) {
      return this.read().some((x) => x.h === handle);
    },
    count() {
      return this.read().length;
    },
    add(handle, id) {
      const list = this.read();
      if (!list.some((x) => x.h === handle)) {
        list.unshift({ h: handle, id: id || null });
        this.write(list);
      }
    },
    remove(handle) {
      this.write(this.read().filter((x) => x.h !== handle));
    },
    toggle(handle, id) {
      this.has(handle) ? this.remove(handle) : this.add(handle, id);
      return this.has(handle);
    },
  };
  window.AoWishlist = Wishlist;

  // 他タブでの変更を同期
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list: Wishlist.read() } }));
    }
  });

  /* ---------------- <wishlist-toggle> ---------------- */
  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.productId = this.dataset.productId;
      this.button = this.querySelector('button');
      if (!this.button || !this.handle) return;
      this.sync();
      this.button.addEventListener('click', this.onClick);
      window.addEventListener(CHANGE_EVENT, this.sync);
    }
    disconnectedCallback() {
      window.removeEventListener(CHANGE_EVENT, this.sync);
    }
    onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      Wishlist.toggle(this.handle, this.productId);
    };
    sync = () => {
      const active = Wishlist.has(this.handle);
      this.classList.toggle('is-active', active);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.button.setAttribute(
        'aria-label',
        active ? 'お気に入りから削除' : 'お気に入りに追加'
      );
      // ラベル付きボタン（商品ページブロック）のテキスト切替
      const label = this.querySelector('.wishlist-toggle__label');
      if (label) {
        label.textContent = active
          ? label.dataset.labelAdded || 'お気に入り済み'
          : label.dataset.labelAdd || 'お気に入りに追加';
      }
    };
  }

  /* ---------------- <wishlist-header-icon> ---------------- */
  class WishlistHeaderIcon extends HTMLElement {
    connectedCallback() {
      this.badge = this.querySelector('[ref="count"]');
      this.sync();
      window.addEventListener(CHANGE_EVENT, this.sync);
    }
    disconnectedCallback() {
      window.removeEventListener(CHANGE_EVENT, this.sync);
    }
    sync = () => {
      const count = Wishlist.count();
      this.classList.toggle('wishlist-has-items', count > 0);
      if (this.badge) {
        this.badge.textContent = count > 99 ? '99+' : String(count);
        this.badge.hidden = count === 0;
      }
    };
  }

  /* ---------------- <wishlist-grid> ---------------- */
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '¥{{amount_no_decimals}}';
      this.grid = this.querySelector('[ref="grid"]');
      this.emptyEl = this.querySelector('[ref="empty"]');
      this.loadingEl = this.querySelector('[ref="loading"]');
      this.render();
      window.addEventListener(CHANGE_EVENT, this.onChange);
    }
    disconnectedCallback() {
      window.removeEventListener(CHANGE_EVENT, this.onChange);
    }
    onChange = () => {
      // 一覧ページでの削除はカード単位で処理済み。他タブ変更時のみ再描画
      const handles = new Set(Wishlist.read().map((x) => x.h));
      this.grid?.querySelectorAll('[data-handle]').forEach((card) => {
        if (!handles.has(card.dataset.handle)) card.remove();
      });
      this.updateEmptyState();
    };

    formatMoney(cents) {
      const amount = cents / 100;
      const noDecimals = Math.round(amount).toLocaleString('ja-JP');
      const withDecimals = amount.toLocaleString('ja-JP', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return this.moneyFormat
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
    }

    async render() {
      const items = Wishlist.read();
      if (this.loadingEl) this.loadingEl.hidden = false;
      if (!items.length) {
        if (this.loadingEl) this.loadingEl.hidden = true;
        this.updateEmptyState();
        return;
      }

      const results = await Promise.all(
        items.map(async (item) => {
          try {
            const res = await fetch(`/products/${item.h}.js`);
            if (!res.ok) throw new Error(String(res.status));
            return { item, product: await res.json() };
          } catch {
            return { item, product: null };
          }
        })
      );

      // 削除済み・非公開商品はストレージからも掃除
      const dead = results.filter((r) => !r.product).map((r) => r.item.h);
      if (dead.length) {
        Wishlist.write(Wishlist.read().filter((x) => !dead.includes(x.h)));
      }

      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.grid) {
        this.grid.innerHTML = '';
        results
          .filter((r) => r.product)
          .forEach(({ product }) => this.grid.appendChild(this.buildCard(product)));
      }
      this.updateEmptyState();
    }

    buildCard(product) {
      const li = document.createElement('li');
      li.className = 'wishlist-card';
      li.dataset.handle = product.handle;

      const img = product.featured_image
        ? product.featured_image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_600x$1$2')
        : '';
      const soldOut = !product.available;

      li.innerHTML = `
        <a class="wishlist-card__link" href="${product.url}">
          <div class="wishlist-card__media">
            ${img ? `<img src="${img}" alt="" loading="lazy" width="600" height="600">` : ''}
            ${soldOut ? '<span class="wishlist-card__soldout">Sold Out</span>' : ''}
          </div>
          <p class="wishlist-card__title"></p>
          <p class="wishlist-card__price">${this.formatMoney(product.price)}</p>
        </a>
        <button type="button" class="wishlist-card__remove" aria-label="お気に入りから削除">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
          </svg>
        </button>
      `;
      li.querySelector('.wishlist-card__title').textContent = product.title;
      li.querySelector('.wishlist-card__remove').addEventListener('click', () => {
        Wishlist.remove(product.handle);
        li.remove();
        this.updateEmptyState();
      });
      return li;
    }

    updateEmptyState() {
      const isEmpty = Wishlist.count() === 0;
      if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
      if (this.grid) this.grid.hidden = isEmpty;
    }
  }

  if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);
  if (!customElements.get('wishlist-header-icon')) customElements.define('wishlist-header-icon', WishlistHeaderIcon);
  if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
})();
